import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Panel from "../../components/ui/Panel/Panel.jsx";
import Button from "../../components/ui/Button/Button.jsx";
import FormField from "../../components/forms/FormField/FormField.jsx";
import TextInput from "../../components/forms/TextInput/TextInput.jsx";
import { login, signup, verifyEmailOtp, resendEmailOtp } from "../../services/authService";
import { isValidEmail } from "../../utils/validators";
import { useApp } from "../../hooks/useApp.js";
import { Eye, EyeOff } from "lucide-react";
import TurnstileWidget, { isCaptchaEnabled } from "../../components/auth/TurnstileWidget.jsx";
import { trackSignup, trackEmailVerified } from "../../seo/track";
import { forgetReferralCode, getRememberedReferralCode } from "../../utils/referral";

// Special-character set accepted for the "special character" requirement:
// the original !@#$%^&* plus [ ] ( ) : ; \ < > " - none of these are
// stripped or rejected anywhere else in this form, so a password made up
// entirely of the newly-added characters (e.g. "Str0ngPass[]") must still
// satisfy this check rather than being silently blocked.
const passwordChecks = {
  length: (p) => p.length >= 8,
  uppercase: (p) => /[A-Z]/.test(p),
  lowercase: (p) => /[a-z]/.test(p),
  number: (p) => /\d/.test(p),
  special: (p) => /[!@#$%^&*[\]():;\\<>"]/.test(p),
};

const passwordLabels = {
  length: "At least 8 characters",
  uppercase: "One uppercase letter",
  lowercase: "One lowercase letter",
  number: "One number",
  special: "One special character (e.g. !@#$%^&* [ ] ( ) : ; \\ < > \")",
};

const Login = () => {
  const { dispatch } = useApp();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") === "up" ? "up" : "in");
  // SessionManager redirects here with ?expired=1 when it force-logs-out
  // because the JWT itself expired (as opposed to a manual sign-out).
  const sessionExpired = searchParams.get("expired") === "1";
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Cloudflare Turnstile token for signup. Empty until the widget solves, and reset to
  // empty on expiry/error and after any failed submit — a Turnstile token is single-use,
  // so re-submitting a spent one is refused by /siteverify.
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileReset, setTurnstileReset] = useState(0);
  // A friend's referral code remembered from their share link (ReferralCapture), if any.
  const [referralCode, setReferralCode] = useState(() => getRememberedReferralCode());
  const removeReferral = () => {
    forgetReferralCode();
    setReferralCode(null);
    setError("");
  };
  const [loginForm, setLoginForm] = useState({
    email: "",
    password: "",
  });
  const [signupForm, setSignupForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    isTaxPreparer: null,
    termsAccepted: false,
  });
  const navigate = useNavigate();
  const isSignUp = tab === "up";

  // Email verification (mandatory for every account - see authService.js's signup()/login()/
  // verifyEmailOtp()). Set once signup() returns a challenge, or login() returns one instead of
  // a token because the account is still unverified; the credentials form is replaced by the
  // code-entry screen below until it's cleared (back or success).
  const [emailChallenge, setEmailChallenge] = useState(null); // { challengeId, maskedEmail, expiresAt, email }
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [resendState, setResendState] = useState("idle"); // 'idle' | 'sending' | 'sent'
  // Ticking countdown to emailChallenge.expiresAt, in whole seconds; 0 once the code has expired.
  const [secondsLeft, setSecondsLeft] = useState(0);
  // Shown on the sign-in tab right after a code is successfully verified, so the user knows
  // why they're back at the credentials form instead of already being signed in.
  const [verifiedNotice, setVerifiedNotice] = useState("");

  // Recomputes secondsLeft once a second while a challenge is active, so the "code expires
  // in mm:ss" display (and the disabled/expired state once it hits 0) stay accurate.
  useEffect(() => {
    if (!emailChallenge?.expiresAt) {
      setSecondsLeft(0);
      return undefined;
    }
    const expiresAtMs = new Date(emailChallenge.expiresAt).getTime();
    const tick = () => {
      setSecondsLeft(Math.max(0, Math.round((expiresAtMs - Date.now()) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [emailChallenge?.expiresAt]);

  const otpExpired = !!emailChallenge?.expiresAt && secondsLeft <= 0;
  const otpCountdown = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")}`;

  const form = isSignUp ? signupForm : loginForm;
  const setForm = isSignUp ? setSignupForm : setLoginForm;

  const update = (field) => (e) => {
    let value = e.target.value;

    if (field === "email") {
      value = value
        .replace(/\s/g, "")
        .replace(/[^\x21-\x7E]/g, "");

      const parts = value.split("@");
      if (parts.length > 2) {
        value = parts[0] + "@" + parts.slice(1).join("");
      }

      value = value.slice(0, 75);
    }

    setForm((f) => ({
      ...f,
      [field]: value,
    }));
  };

  const passwordResults = Object.keys(passwordChecks).map((key) => ({
    key,
    label: passwordLabels[key],
    valid: passwordChecks[key](form.password),
  }));
  const passwordValid = passwordResults.every((r) => r.valid);

  const switchTab = (next) => {
    setTab(next);
    setError("");
    setTouched(false);
    setVerifiedNotice("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (isSignUp) {
      setTouched(true);

      if (
        !isValidEmail(form.email) ||
        !passwordValid ||
        form.isTaxPreparer === null ||
        !form.termsAccepted
      ) {
        return;
      }
    } else {
      if (!isValidEmail(form.email)) {
        setTouched(true);
        return;
      }
    }
    setSubmitting(true);
    try {
      if (isSignUp) {
        const result = await signup({ ...signupForm, turnstileToken, referralCode });
        forgetReferralCode();
        // Account is created but unverified - no token yet. Stash where a self-identified
        // tax preparer should land right after their first real sign-in (Tax Preparer is no
        // longer a wizard step - see stepConfig.js - they go straight to the standalone
        // preparer form, same one TaxPreparers.jsx's "Add Preparer" uses).
        if (signupForm.isTaxPreparer) {
          sessionStorage.setItem("postVerifyRoute", "/dashboard/preparers/form");
        } else {
          sessionStorage.removeItem("postVerifyRoute");
        }
        // Conversion: the API accepted the signup and created the account. Fired
        // here rather than on button click so it counts real accounts, not attempts.
        trackSignup({ tax_preparer: !!signupForm.isTaxPreparer });
        setEmailChallenge({ ...result, email: signupForm.email });
      } else {
        const result = await login({ email: loginForm.email, password: loginForm.password });
        if (result.token) {
          // Clears any businessId/preparerId/returnId a previous account left
          // behind in this tab's sessionStorage (see AppContext.jsx's
          // PERSISTED_ID_KEYS) - without this, a fresh sign-in could silently
          // inherit and display the last account's business/trucks.
          dispatch({ type: "SEED", which: "new" });
          dispatch({ type: "SET_IS_TAX_PREPARER", isTaxPreparer: !!result?.user?.taxPreparer });
          const postVerifyRoute = sessionStorage.getItem("postVerifyRoute");
          sessionStorage.removeItem("postVerifyRoute");
          navigate(postVerifyRoute || "/dashboard");
        } else {
          // Password was correct, but this account's email still isn't verified - same
          // code-entry screen signup() starts.
          setEmailChallenge({ ...result, email: loginForm.email });
        }
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      // The token was consumed by that attempt whatever the failure was, so arm a fresh
      // challenge rather than leaving a spent one behind a disabled button.
      if (isSignUp && isCaptchaEnabled()) {
        setTurnstileToken("");
        setTurnstileReset((n) => n + 1);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Consumes the code just emailed for emailChallenge. Never signs the user in directly -
  // it sends them back to the sign-in tab so they sign in for real with the now-verified email.
  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    if (otpExpired) {
      setOtpError("This code has expired. Request a new one below.");
      return;
    }
    if (!/^\d{6}$/.test(otpCode)) {
      setOtpError("Enter the 6-digit code we emailed you.");
      return;
    }
    setOtpError("");
    setOtpSubmitting(true);
    try {
      await verifyEmailOtp({ challengeId: emailChallenge.challengeId, code: otpCode });
      // Conversion: the emailed code worked, so the account is now usable. The gap
      // between this and trackSignup is the OTP-delivery funnel.
      trackEmailVerified();
      setLoginForm((f) => ({ ...f, email: emailChallenge.email || f.email, password: "" }));
      setEmailChallenge(null);
      setOtpCode("");
      setResendState("idle");
      setVerifiedNotice("Your email is verified. Please sign in.");
      setTab("in");
    } catch (err) {
      setOtpError(err.message || "That code doesn't match. Try again.");
    } finally {
      setOtpSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    setResendState("sending");
    try {
      const result = await resendEmailOtp({ challengeId: emailChallenge.challengeId });
      // Fresh code means a fresh expiresAt too - swap it in so the countdown restarts
      // instead of continuing to tick down toward the old (now-discarded) code's expiry.
      setEmailChallenge((c) => ({ ...c, ...result }));
      setOtpCode("");
      setOtpError("");
      setResendState("sent");
    } catch (err) {
      setResendState("idle");
      setOtpError(err.message || "Couldn't resend the code. Please try again.");
    }
  };

  const backToCredentials = () => {
    setEmailChallenge(null);
    setOtpCode("");
    setOtpError("");
    setResendState("idle");
  };

  if (emailChallenge) {
    return (
      <Panel padded className="w-full max-w-[400px] !p-7">
        <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
          Verify your email
        </div>
        <h1 className="text-2xl mt-1.5">Enter your code</h1>
        <p className="text-[var(--muted)] my-1 mb-[22px]">
          We sent a 6-digit code to <strong className="text-[var(--ink)]">{emailChallenge.maskedEmail}</strong>. Enter
          it below to verify your email.
        </p>

        <form onSubmit={handleVerifyEmail}>
          <FormField label="Verification code" error={otpError} showError={!!otpError}>
            <TextInput
              type="text"
              inputMode="numeric"
              mono
              placeholder="123456"
              maxLength={6}
              value={otpCode}
              onChange={(e) => {
                setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                setOtpError("");
              }}
              invalid={!!otpError}
              disabled={otpExpired}
              autoFocus
            />
          </FormField>

          {emailChallenge.expiresAt && (
            <div className={["text-xs mb-3", otpExpired ? "text-[var(--danger)]" : "text-[var(--muted)]"].join(" ")}>
              {otpExpired ? "This code has expired. Request a new one below." : `Code expires in ${otpCountdown}`}
            </div>
          )}

          <Button type="submit" size="lg" block disabled={otpSubmitting || otpExpired}>
            {otpSubmitting ? "Verifying…" : "Verify email"}
          </Button>
        </form>

        <p className="text-[var(--muted)] text-xs text-center mt-3.5">
          {resendState === "sent" ? (
            "A new code is on its way."
          ) : (
            <>
              Didn't get it?{" "}
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={resendState === "sending"}
                className="text-[var(--accent)] font-semibold underline disabled:opacity-60"
              >
                {resendState === "sending" ? "Sending…" : "Resend code"}
              </button>
            </>
          )}
        </p>
        <p className="text-[var(--muted)] text-xs text-center mt-1.5">
          <button type="button" onClick={backToCredentials} className="text-[var(--accent)] font-semibold underline">
            ← Back
          </button>
        </p>
      </Panel>
    );
  }

  return (
    <Panel padded className="w-full max-w-[400px] !p-7">
      <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
        Heavy Vehicle Use Tax · TY2026
      </div>
      <h1 className="text-2xl mt-1.5">
        {isSignUp ? "Create your account" : "Welcome back"}
      </h1>
      <p className="text-[var(--muted)] my-1 mb-[22px]">
        File Form 2290 and get your stamped Schedule 1 — usually within seconds.
      </p>

      <div className="flex gap-1.5 mb-[18px] border-b border-[var(--line)]">
        <TabButton active={tab === "in"} onClick={() => switchTab("in")}>
          Sign in
        </TabButton>
        <TabButton active={tab === "up"} onClick={() => switchTab("up")}>
          Create account
        </TabButton>
      </div>

      {!isSignUp && verifiedNotice && (
        <div className="text-xs text-[var(--ok)] mb-3">{verifiedNotice}</div>
      )}
      {!isSignUp && !verifiedNotice && sessionExpired && (
        <div className="text-xs text-[var(--amber)] mb-3">Your session expired. Please sign in again.</div>
      )}

      {/* noValidate: the email field intentionally accepts IRS-legal
          characters ()':;<>[]\ (see isValidEmail/LOGIN_EMAIL_CHARS in
          validators.js) that fall outside the browser's native
          type="email" constraint-validation grammar - without this, the
          browser blocks submission (or shows its own "invalid email"
          popup) before handleSubmit's own validation ever runs, even
          though our own check accepts the address. */}
      <form onSubmit={handleSubmit} noValidate>
        {/* {isSignUp && (
          <div className="grid grid-cols-2 gap-3">
            <FormField label="First name">
              <TextInput
                placeholder="Pat"
                value={form.firstName}
                onChange={update("firstName")}
                required
              />
            </FormField>
            <FormField label="Last name">
              <TextInput
                placeholder="Driver"
                value={form.lastName}
                onChange={update("lastName")}
                required
              />
            </FormField>
          </div>
        )} */}
        <FormField
          label={
            <>
              Email <span className="text-red-500">*</span>
            </>
          }
          error="Enter a valid email address."
          showError={touched && !isValidEmail(form.email)}
        >
          <TextInput
            type="email"
            placeholder="you@company.com"
            value={form.email}
            onChange={update("email")}
            invalid={touched && !isValidEmail(form.email)}
            required
          />
        </FormField>
    <FormField
  label={
    <>
      Password <span className="text-red-500">*</span>
    </>
  }
>
  <div className="relative">
    <TextInput
      type={showPassword ? "text" : "password"}
      value={form.password}
      onChange={update("password")}
      required
      minLength={isSignUp ? 8 : undefined}
      invalid={isSignUp && touched && !passwordValid}
      className="pr-10"
    />

    <button
      type="button"
      onClick={() => setShowPassword((prev) => !prev)}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
      tabIndex={-1}
    >
      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  </div>
</FormField>
        {isSignUp && (
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 -mt-2.5 mb-4">
            {passwordResults.map((r) => (
              <div
                key={r.key}
                className={[
                  "text-xs",
                  r.valid ? "text-[var(--ok)]" : "text-[var(--muted)]",
                ].join(" ")}
              >
                {r.valid ? "✓" : "•"} {r.label}
              </div>
            ))}
          </div>
        )}
        {isSignUp && (
          <FormField
            label={
              <>
                Are you a tax preparer? <span className="text-red-500">*</span>
              </>
            }
          >
            <div className="flex gap-4">
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="isTaxPreparer"
                  checked={form.isTaxPreparer === true}
                  onChange={() =>
                    setForm((f) => ({
                      ...f,
                      isTaxPreparer: true,
                    }))
                  }
                />
                Yes
              </label>

              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="isTaxPreparer"
                  checked={form.isTaxPreparer === false}
                  onChange={() =>
                    setForm((f) => ({
                      ...f,
                      isTaxPreparer: false,
                    }))
                  }
                />
                No
              </label>
            </div>
          </FormField>
        )}
        {isSignUp && (
          <div className="mb-4">
            <label className="flex items-start gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.termsAccepted}
                onChange={(e) => setForm((f) => ({ ...f, termsAccepted: e.target.checked }))}
                className="mt-[3px]"
              />
              <span className={["text-xs", touched && !form.termsAccepted ? "text-[var(--danger)]" : "text-[var(--muted)]"].join(" ")}>
                I agree to the{" "}
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-[var(--accent)] font-semibold underline"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-[var(--accent)] font-semibold underline"
                >
                  Privacy Policy
                </Link>
                .
              </span>
            </label>
            {touched && !form.termsAccepted && (
              <div className="text-xs text-[var(--danger)] mt-1">Please accept the Terms of Service and Privacy Policy.</div>
            )}
            {referralCode && (
              <div className="flex items-center justify-between gap-2 text-xs text-[var(--muted)] mt-3 border border-[var(--line)] rounded-[var(--r-sm)] px-3 py-2 bg-[var(--panel-2)]">
                <span>You're signing up with a friend's referral link.</span>
                <button
                  type="button"
                  onClick={removeReferral}
                  className="text-[var(--accent)] bg-transparent border-0 cursor-pointer font-semibold p-0"
                >
                  Remove
                </button>
              </div>
            )}
            {/* Renders nothing until REACT_APP_TURNSTILE_SITE_KEY is set, so signup is
                unchanged until a key is provisioned. The server is the real check. */}
            <TurnstileWidget onToken={setTurnstileToken} resetSignal={turnstileReset} className="mt-3" />
          </div>
        )}
        {/* {isSignUp && (
          <FormField label="Confirm password">
            <TextInput
              type="password"
              value={form.confirmPassword}
              onChange={update("confirmPassword")}
              required
            />
          </FormField>
        )} */}

        {error && (
          <div className="text-xs text-[var(--danger)] mb-3">{error}</div>
        )}

        <Button
          type="submit"
          size="lg"
          block
          disabled={
            submitting ||
            (isSignUp &&
              (!passwordValid ||
                form.isTaxPreparer === null ||
                !form.termsAccepted ||
                // Only blocks when a site key is configured; inert otherwise.
                (isCaptchaEnabled() && !turnstileToken)))
          }
        >
          {submitting
            ? "Please wait…"
            : isSignUp
              ? "Create account"
              : "Sign in"}
        </Button>
      </form>

      {!isSignUp && (
        <p className="text-[var(--muted)] text-xs text-center mt-3.5">
          Forgot your password?{" "}
          <Link
            to={loginForm.email ? `/reset-password?email=${encodeURIComponent(loginForm.email)}` : "/reset-password"}
            className="text-[var(--accent)] font-semibold underline"
          >
            Reset it
          </Link>
        </p>
      )}
    </Panel>
  );
};

const TabButton = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      "border-0 bg-transparent font-semibold py-[9px] px-1 cursor-pointer border-b-2 -mb-px",
      active
        ? "text-[var(--ink)] border-[var(--accent)]"
        : "text-[var(--muted)] border-transparent",
    ].join(" ")}
  >
    {children}
  </button>
);

export default Login;
